import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/api/client'
import type {
  CrewOption,
  PayPeriod,
  PerDiemRegister,
  PerDiemRegisterPerson,
} from '@/api/types'

/**
 * Per diem HQ.
 *
 * NW Lining runs per diem as its own payroll, alongside reimbursements and
 * deductions. Eligibility is not something the PMs track — distance from home,
 * hours on a prevailing site — it is payroll's judgement and the Field Ops
 * Manager's to approve. So this screen deliberately sits outside the hours
 * approval flow and answers one question: what per diem was recorded, and what
 * should come off.
 *
 * THE LAYOUT IS THE WORKBOOK. Blocks headed by crew and job, one row per
 * person, one column per day, a total on the right. The whole period on one
 * row is what makes the case this screen exists for — somebody claimed for
 * seven days sitting beside colleagues on two — visible without anyone running
 * a query. A per-person drill-down would hide exactly the comparison that
 * matters.
 *
 * ONE GRID, VARIABLE WIDTH. A week is seven columns and a month is thirty-one,
 * scrolling sideways. A separate read-only summary for the longer period was
 * the obvious alternative and is worse: a correction would then be possible
 * from one view and not the other, and the reviewer would have to change
 * screens between spotting a problem and fixing it.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAY = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

/** Parsed as parts — `new Date('2026-08-15')` is UTC midnight and renders as
 *  the 14th west of Greenwich, which is where these crews are. */
function parts(iso: string) {
  const [y, m, d] = iso.split('-').map(Number)
  return { y, m, d, dow: new Date(y, m - 1, d).getDay() }
}
function dayLabel(iso: string): string {
  const { y, m, d, dow } = parts(iso)
  return `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dow]} ${d} ${MONTHS[m - 1]} ${y}`
}
function money(v: string | number | null | undefined): string {
  return `$${Number(v ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`
}

/** Same tier colours the crew week uses, so one person reads the same on both. */
const TIER_CLASS: Record<string, string> = {
  Super: 'role-super', QC: 'role-qc', Technician: 'role-tech',
}

type Period = 'week' | 'month'
type GroupBy = 'crew' | 'job'

export function PerDiemRegisterPage() {
  const [periods, setPeriods] = useState<PayPeriod[]>([])
  const [crews, setCrews] = useState<CrewOption[]>([])

  const [period, setPeriod] = useState<Period>('week')
  const [weekEnding, setWeekEnding] = useState('')
  const [month, setMonth] = useState('')
  const [groupBy, setGroupBy] = useState<GroupBy>('crew')
  const [crew, setCrew] = useState<number | null>(null)
  const [jobNo, setJobNo] = useState<string>('')

  const [reg, setReg] = useState<PerDiemRegister | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  /** Ticked day cells, by TimeEntryId, with what removing them would save. */
  const [picked, setPicked] = useState<Map<string, number>>(new Map())
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void (async () => {
      const [p, c] = await Promise.allSettled([api.payPeriods(), api.crewOptions()])
      if (p.status === 'fulfilled') setPeriods(p.value)
      if (c.status === 'fulfilled') setCrews(c.value)
    })()
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await api.perDiemRegister({
        weekEnding: period === 'week' ? weekEnding || undefined : undefined,
        month: period === 'month' ? month || undefined : undefined,
        groupBy,
        crew,
        jobNo: jobNo || null,
      })
      setReg(r)
      if (period === 'week' && !weekEnding) setWeekEnding(r.period_end)
      if (period === 'month' && !month) setMonth(r.period_start.slice(0, 7))
      // Anything ticked belongs to the view that was on screen, not this one.
      setPicked(new Map())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the register')
      setReg(null)
    } finally {
      setLoading(false)
    }
  }, [period, weekEnding, month, groupBy, crew, jobNo])

  useEffect(() => {
    void load()
  }, [load])

  const pickedTotal = useMemo(
    () => [...picked.values()].reduce((a, b) => a + b, 0), [picked],
  )

  function toggleDay(id: string, amount: string) {
    setPicked((prev) => {
      const next = new Map(prev)
      if (next.has(id)) next.delete(id)
      else next.set(id, Number(amount))
      return next
    })
  }

  /** Tick every editable day this person has — the "7 down to 2" case starts
   *  by taking the whole row and putting two back. */
  function toggleRow(p: PerDiemRegisterPerson) {
    const cells = p.days.filter((d): d is NonNullable<typeof d> => !!d && d.is_editable)
    const allOn = cells.length > 0 && cells.every((c) => picked.has(c.time_entry_id))
    setPicked((prev) => {
      const next = new Map(prev)
      for (const c of cells) {
        if (allOn) next.delete(c.time_entry_id)
        else next.set(c.time_entry_id, Number(c.amount))
      }
      return next
    })
  }

  async function removePicked() {
    if (picked.size === 0) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const result = await api.removePerDiemDays(
        [...picked.keys()], reason.trim() || undefined)
      setNotice(
        `Removed ${result.removed} day${result.removed === 1 ? '' : 's'} for ` +
          `${result.people} ${result.people === 1 ? 'person' : 'people'} — ` +
          `${money(result.total_amount)} off the period.`)
      setReason('')
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message
        : 'Could not remove those days. Nothing was changed.')
    } finally {
      setBusy(false)
    }
  }

  /** Months worth offering: the twelve ending with the newest pay period. */
  const monthOptions = useMemo(() => {
    const anchor = periods[0]?.week_ending ?? new Date().toISOString().slice(0, 10)
    const { y, m } = parts(anchor)
    return Array.from({ length: 12 }, (_, i) => {
      const mm = m - i
      const yy = y + Math.floor((mm - 1) / 12)
      const norm = ((mm - 1) % 12 + 12) % 12
      return { value: `${yy}-${String(norm + 1).padStart(2, '0')}`,
               label: `${MONTHS[norm]} ${yy}` }
    })
  }, [periods])

  const jobsInView = useMemo(() => {
    const seen = new Map<string, string>()
    for (const b of reg?.blocks ?? []) seen.set(b.job_no, b.job_description ?? '')
    return [...seen.entries()].sort()
  }, [reg])

  const wholePeriodPaid = reg?.is_paid === true
  const somePaid = (reg?.paid_weeks.length ?? 0) > 0

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-ok">{notice}</div>}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Per diem</h2>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label htmlFor="period">Show</label>
            <select
              id="period"
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
              disabled={loading || busy}
            >
              <option value="week">A week</option>
              <option value="month">A month</option>
            </select>
          </div>

          {period === 'week' ? (
            <div className="form-field" style={{ marginBottom: 0, minWidth: 200 }}>
              <label htmlFor="week">Week ending</label>
              <select
                id="week"
                value={weekEnding}
                onChange={(e) => setWeekEnding(e.target.value)}
                disabled={loading || busy}
              >
                {/* The server's week may predate the generated list, so it is
                    offered explicitly rather than silently missing. */}
                {weekEnding && !periods.some((p) => p.week_ending === weekEnding) && (
                  <option value={weekEnding}>{dayLabel(weekEnding)}</option>
                )}
                {periods.map((p) => (
                  <option key={p.week_ending} value={p.week_ending}>
                    {dayLabel(p.week_ending)}{p.paid_in_sage ? ' — paid' : ''}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="form-field" style={{ marginBottom: 0, minWidth: 160 }}>
              <label htmlFor="month">Month</label>
              <select
                id="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                disabled={loading || busy}
              >
                {monthOptions.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-field" style={{ marginBottom: 0 }}>
            <label htmlFor="groupBy">Group by</label>
            <select
              id="groupBy"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              disabled={loading || busy}
            >
              <option value="crew">Crew</option>
              <option value="job">Job</option>
            </select>
          </div>

          <div className="form-field" style={{ marginBottom: 0, minWidth: 170 }}>
            <label htmlFor="crewFilter">Crew</label>
            <select
              id="crewFilter"
              value={crew ?? ''}
              onChange={(e) => setCrew(e.target.value ? Number(e.target.value) : null)}
              disabled={loading || busy}
            >
              <option value="">All crews</option>
              {crews.map((c) => (
                <option key={c.supervisor_employee_id} value={c.supervisor_employee_id}>
                  {c.supervisor_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field" style={{ marginBottom: 0, minWidth: 170 }}>
            <label htmlFor="jobFilter">Job</label>
            <select
              id="jobFilter"
              value={jobNo}
              onChange={(e) => setJobNo(e.target.value)}
              disabled={loading || busy}
            >
              <option value="">All jobs</option>
              {/* Only jobs that appear in the current view — a picker of 318
                  open jobs, almost none carrying per diem, would be noise. */}
              {jobsInView.map(([no, desc]) => (
                <option key={no} value={no}>{no} {desc.slice(0, 28)}</option>
              ))}
              {jobNo && !jobsInView.some(([no]) => no === jobNo) && (
                <option value={jobNo}>{jobNo}</option>
              )}
            </select>
          </div>

          {(crew !== null || jobNo) && (
            <button
              className="btn btn-secondary"
              onClick={() => { setCrew(null); setJobNo('') }}
            >
              Clear filters
            </button>
          )}
        </div>

        {reg && (
          <p className="muted" style={{ marginTop: '0.9rem', marginBottom: 0 }}>
            <strong>{reg.period_label}</strong> · {money(reg.total_amount)} ·{' '}
            {reg.total_days} day{reg.total_days === 1 ? '' : 's'} ·{' '}
            {reg.people_count} {reg.people_count === 1 ? 'person' : 'people'}
          </p>
        )}
      </div>

      {wholePeriodPaid ? (
        <div className="alert alert-warn">
          Sage paid this period{reg?.check_date ? ` on ${dayLabel(reg.check_date)}` : ''}.
          It is shown for reference only — once payroll has run the portal has no
          authority to change it, and a correction has to be made in Sage.
        </div>
      ) : somePaid ? (
        <div className="alert alert-warn">
          Part of this period has already been paid
          {reg && ` (week${reg.paid_weeks.length === 1 ? '' : 's'} ending ` +
            `${reg.paid_weeks.map(dayLabel).join(', ')})`}
          . Those days are shown greyed and cannot be ticked.
        </div>
      ) : null}

      {loading && <p className="muted">Loading…</p>}

      {reg && !loading && reg.blocks.length === 0 && (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            No per diem recorded{crew !== null || jobNo ? ' for that filter' : ''} in{' '}
            {reg.period_label.toLowerCase()}.
          </p>
        </div>
      )}

      {reg && !loading && reg.blocks.length > 0 && (
        <div className="card" style={{ paddingTop: '0.85rem', paddingBottom: '0.85rem' }}>
          <p className="muted" style={{ margin: 0, fontSize: '0.875rem' }}>
            <strong>Tick the days that should not have been claimed</strong>, then
            remove them with the bar at the bottom. Clicking a person's name ticks
            their whole row. Greyed figures are already paid and cannot be changed.
          </p>
        </div>
      )}

      {reg?.blocks.map((b) => (
        <div className="card" key={`${b.supervisor_employee_id ?? 'x'}|${b.job_no}`}>
          <div className="crew-heading">
            <h2 style={{ margin: 0 }}>
              {reg.grouped_by === 'job' ? (
                <>
                  <span className="mono">{b.job_no}</span>{' '}
                  <span style={{ fontWeight: 400 }}>{b.job_description}</span>
                </>
              ) : (
                b.supervisor_name
              )}
            </h2>
            <span className="muted">
              {b.block_days} day{b.block_days === 1 ? '' : 's'} ·{' '}
              <strong>{money(b.block_total)}</strong>
            </span>
          </div>
          <p className="muted" style={{ marginTop: 0, fontSize: '0.8125rem' }}>
            {reg.grouped_by === 'job' ? (
              <>{b.people.length} people across all crews</>
            ) : (
              <>
                <span className="mono">{b.job_no}</span> {b.job_description}
              </>
            )}
            {b.state_code && ` · ${b.state_code}`}
            {b.per_diem_location && (
              <> · <span className="badge">{b.per_diem_location}</span> site rate</>
            )}
          </p>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Employee #</th>
                  {reg.grouped_by === 'job' && <th>Crew</th>}
                  <th>State of res.</th>
                  <th>Classification</th>
                  <th className="num">Rate</th>
                  {reg.days.map((d) => {
                    const { d: dd, dow } = parts(d)
                    return (
                      <th key={d} className="num" title={dayLabel(d)}>
                        {/* A month is too wide for weekday names; the initial
                            plus the date is enough to find a Saturday. */}
                        {reg.days.length > 7
                          ? <>{WEEKDAY[dow]}<br />{dd}</>
                          : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dow]}
                      </th>
                    )
                  })}
                  <th className="num">Days</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {b.people.map((p) => {
                  const editable = p.days.filter((d) => d?.is_editable).length
                  return (
                    <tr key={p.sage_employee_key}>
                      <td>
                        {editable > 0 ? (
                          <button
                            className="linklike"
                            onClick={() => toggleRow(p)}
                            title="Tick every day for this person"
                          >
                            {p.display_name}
                          </button>
                        ) : (
                          p.display_name
                        )}
                      </td>
                      <td className="mono">{p.employee_no}</td>
                      {reg.grouped_by === 'job' && (
                        <td className="muted">{p.crew_name}</td>
                      )}
                      <td>
                        {p.residence_state || p.residence_city ? (
                          <>
                            {p.residence_state ?? ''}
                            {p.residence_city ? ` (${p.residence_city})` : ''}
                            {p.residence_note && (
                              <div className="muted" style={{ fontSize: '0.75rem' }}>
                                {p.residence_note}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="muted" title="Not recorded — set it on the Employees screen">—</span>
                        )}
                      </td>
                      <td>
                        {p.tier ? (
                          <span className={`badge ${TIER_CLASS[p.tier] ?? ''}`}>{p.tier}</span>
                        ) : (
                          <span className="badge badge-warn">no tier</span>
                        )}
                      </td>
                      <td className="num">
                        {p.rate === null ? (
                          <span className="muted" title="More than one rate in this period">mixed</span>
                        ) : money(p.rate)}
                      </td>
                      {p.days.map((d, i) => (
                        <td key={reg.days[i]} className="num">
                          {!d ? (
                            <span className="muted">·</span>
                          ) : d.is_editable ? (
                            <label
                              style={{ cursor: 'pointer', display: 'inline-flex', gap: 4 }}
                              title={`${dayLabel(reg.days[i])} · ${money(d.amount)}`}
                            >
                              <input
                                type="checkbox"
                                style={{ minHeight: 'auto' }}
                                checked={picked.has(d.time_entry_id)}
                                onChange={() => toggleDay(d.time_entry_id, d.amount)}
                                aria-label={`Remove ${p.display_name} ${dayLabel(reg.days[i])} ${money(d.amount)}`}
                              />
                              {Number(d.amount).toFixed(0)}
                            </label>
                          ) : (
                            // Paid or posted. Shown, not hidden: the money was
                            // real and dropping it would misstate the period.
                            <span className="muted" title="Already paid or posted to Sage">
                              {Number(d.amount).toFixed(0)}
                            </span>
                          )}
                        </td>
                      ))}
                      <td className="num">{p.days_count}</td>
                      <td className="num">{money(p.total)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Sticks to the bottom while you work down the blocks. */}
      {picked.size > 0 && (
        <div className="card" style={{ position: 'sticky', bottom: 0, borderColor: 'var(--c-brand)' }}>
          <div className="crew-heading">
            <h2 style={{ margin: 0 }}>
              {picked.size} day{picked.size === 1 ? '' : 's'} selected ·{' '}
              <span style={{ color: 'var(--c-error)' }}>−{money(pickedTotal)}</span>
            </h2>
            <button className="btn btn-secondary" onClick={() => setPicked(new Map())}>
              Clear
            </button>
          </div>
          <div className="form-field">
            <label htmlFor="reason">Reason (optional, kept on the audit trail)</label>
            <input
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. only eligible Mon–Tue, went home midweek"
              maxLength={300}
            />
          </div>
          <button className="btn btn-block" disabled={busy} onClick={() => void removePicked()}>
            {busy ? 'Removing…'
              : `Remove ${picked.size} day${picked.size === 1 ? '' : 's'} · −${money(pickedTotal)}`}
          </button>
        </div>
      )}
    </div>
  )
}
